import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { Box, Button, ButtonBase, Tooltip, Typography } from '@mui/material';
import { useState } from 'react';
import { dayAgo } from '../work/workFolderUi';
import { STATUS_ICONS } from './TaskStatusChip';
import { formatDue, formatTime, shortName } from './taskUi';

const StageStep = ({ stage, current, moveLabel, onMove, editorLevel }) => {
    const done = stage.status === 'COMPLETED' || stage.status === 'SKIPPED';
    const statusKey =
        stage.status === 'BLOCKED' ? 'BLOCKED' : stage.status === 'IN_PROGRESS' ? 'IN_PROGRESS' : done ? 'DONE' : null;
    const Icon = statusKey
        ? STATUS_ICONS[{ BLOCKED: 'block', IN_PROGRESS: 'play', DONE: 'check' }[statusKey]]
        : RadioButtonUncheckedIcon;
    const who = stage.assignedTo || stage.plannedAssignee;
    const step = (
        <Box
            component={onMove ? ButtonBase : 'div'}
            onClick={onMove}
            sx={{
                display: 'flex',
                width: '100%',
                textAlign: 'left',
                alignItems: 'flex-start',
                justifyContent: 'flex-start',
                gap: 1,
                py: 0.75,
                px: 1,
                borderRadius: 1,
                backgroundColor: current ? 'action.selected' : 'transparent',
                '&:hover': onMove ? { backgroundColor: 'action.hover' } : undefined,
            }}
            aria-current={current ? 'step' : undefined}
            aria-label={onMove ? `${moveLabel}: ${stage.displayName}` : undefined}
        >
            <Icon
                sx={{ fontSize: '1.1rem', mt: 0.25 }}
                color={stage.status === 'BLOCKED' ? 'error' : done ? 'success' : current ? 'primary' : 'disabled'}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: current ? 600 : 400 }}>
                    {stage.displayName}
                    {stage.status === 'SKIPPED' && ' (skipped)'}
                    {stage.auto && (
                        <Typography component="span" variant="caption" color="text.secondary">
                            {' '}
                            {editorLevel ? `· ${editorLevel} in the editor` : '· from editor'}
                        </Typography>
                    )}
                </Typography>
                <Typography variant="caption" color="text.secondary" component="div">
                    {who ? shortName(who) : stage.assignedTo === null && current ? 'Unassigned' : ''}
                    {!stage.assignedTo && stage.plannedAssignee ? ' (planned)' : ''}
                    {stage.startedByUpload && stage.status === 'IN_PROGRESS'
                        ? ` · started with the first upload ${dayAgo(stage.startedAt)}`
                        : ''}
                    {stage.startedByConversion && stage.status === 'IN_PROGRESS'
                        ? ` · started with New Dubbing Audio ${dayAgo(stage.startedAt)}`
                        : ''}
                    {stage.dueDate && !done ? ` · due ${formatDue(stage.dueDate)}` : ''}
                    {stage.completedAt && done ? ` · ${formatTime(stage.completedAt)}` : ''}
                    {stage.reworkRequired ? ' · waiting for the editor to reopen' : ''}
                </Typography>
            </Box>
        </Box>
    );
    return onMove ? (
        <Tooltip title={moveLabel} placement="left">
            {step}
        </Tooltip>
    ) : (
        step
    );
};

const isDone = (s) => s.status === 'COMPLETED' || s.status === 'SKIPPED';

/**
 * The stages, top to bottom. The done ones fold for everyone; a PM opens them to click one and send
 * the task back.
 */
const TaskStages = ({ task, stages, link, canManage, moveFor }) => {
    const [doneOpen, setDoneOpen] = useState(false);
    const doneCount = stages.filter(isDone).length;
    const folds = doneCount > 0;
    const shown = folds && !doneOpen ? stages.filter((s) => !isDone(s)) : stages;
    return (
        <>
            <Typography variant="overline" component="div" sx={{ mt: 2 }}>
                Stages
            </Typography>
            {folds && (
                <Button
                    size="small"
                    color="inherit"
                    onClick={() => setDoneOpen((o) => !o)}
                    endIcon={doneOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    aria-expanded={doneOpen}
                    sx={{ px: 1 }}
                >
                    {doneCount} done
                </Button>
            )}
            <Box>
                {shown.map((s) => {
                    const move = moveFor(s);
                    return (
                        <StageStep
                            key={s.stageKey}
                            stage={s}
                            editorLevel={link?.linkedStages?.[s.stageKey]}
                            current={s.stageKey === task.currentStageKey}
                            moveLabel={move?.label}
                            onMove={move?.go}
                        />
                    );
                })}
            </Box>
            {canManage && stages.some((s) => moveFor(s)) && (
                <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                    Click a stage to send the task back or move it on.
                </Typography>
            )}
        </>
    );
};

export default TaskStages;
